/**
 * People — «alle mennesker i arrangementet» (PAGES.md, People [F2]).
 *
 * Two things, one page: BEMANNING (who is in this year, in which department, with which badge) and
 * SKIFT (time slots with a need and the people on them). «Ledige skift synlig» is `needed` minus
 * the number assigned, computed by the query rather than stored — a stored count is a count that
 * disagrees with its rows.
 *
 * Everybody here is a reference to Contacts. There is no name field anywhere in this file.
 */
import { z } from 'zod';

import { contextAllocationSchema, handOutModeSchema } from './resources.js';

import { cursorQuerySchema, page } from '../http/pagination.js';
import { rowViewerSchema } from './row-viewer.js';
import { writeWarningsField } from './write-warnings.js';

export const CREW_SORT_KEYS = ['name'] as const;
export type CrewSortKey = (typeof CREW_SORT_KEYS)[number];

const roleTitleSchema = z.string().trim().min(1).max(120);

/** One person's place in one årgang. The person's own fields come from Contacts, never from here. */
export const crewMemberDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    personId: z.uuid(),
    /** Resolved for display; null when the contact was removed from the register. */
    displayName: z.string().nullable(),
    contactId: z.uuid().nullable(),
    departmentId: z.uuid().nullable(),
    departmentName: z.string().nullable(),
    roleTitle: z.string().nullable(),
    accreditationLevelId: z.uuid().nullable(),
    accreditationLevelName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'CrewMember' });

export const listCrewQuerySchema = cursorQuerySchema(CREW_SORT_KEYS)
  .extend({
    editionId: z.uuid(),
    departmentId: z.uuid().optional(),
  })
  .meta({ id: 'ListCrewQuery' });

export const crewListResponseSchema = page(crewMemberDtoSchema, 'CrewListResponse');

export const addCrewMemberRequestSchema = z
  .object({
    editionId: z.uuid(),
    personId: z.uuid(),
    departmentId: z.uuid().nullish(),
    roleTitle: roleTitleSchema.nullish(),
    accreditationLevelId: z.uuid().nullish(),
  })
  .meta({ id: 'AddCrewMemberRequest' });

/** `personId` is absent: staffing a different person is a different row, not an edit of this one. */
export const updateCrewMemberRequestSchema = z
  .object({
    departmentId: z.uuid().nullish(),
    roleTitle: roleTitleSchema.nullish(),
    accreditationLevelId: z.uuid().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateCrewMemberRequest' });

// -------------------------------------------------------------------------------------- poster

/**
 * Poster (PEOPLE.md, fane 1) — «en post/rolle hører til en avdeling».
 *
 * The timeline groups by department and lists posts beneath it, so a post is a ROW rather than a
 * repeated string. Configured in Settings like everything else on this page (principle 3).
 */
/** «Deles ut på vakta» — én ressurstype posten gir den som står der (0357). */
export const staffingRoleHandOutSchema = z
  .object({
    resourceTypeId: z.uuid(),
    resourceTypeName: z.string(),
    /** `person`: én til hver på vakta. `shift`: én til vakta. */
    per: handOutModeSchema.exclude(['none']),
  })
  .meta({ id: 'StaffingRoleHandOut' });
export type StaffingRoleHandOut = z.infer<typeof staffingRoleHandOutSchema>;

export const staffingRoleDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null is the organisation-level template — the same two scopes departments have. */
    editionId: z.uuid().nullable(),
    /** Null when the post sits on a resource that has no department this year (0357). */
    departmentId: z.uuid().nullable(),
    departmentName: z.string().nullable(),
    key: z.string(),
    name: z.string(),
    defaultNeeded: z.number().int().positive(),
    sortOrder: z.number().int(),
    /*
     * WHO OWNS THE POST (0357, sak 197). A place, a type, or neither:
     *   resourceId      «Bartender» at Bar stornaust — staffed while it is open
     *   resourceTypeId  «Sjåfør» on every use of Kjøretøy
     *   both null       a loose post in the department («Runner»)
     */
    resourceId: z.uuid().nullable(),
    resourceName: z.string().nullable(),
    resourceTypeId: z.uuid().nullable(),
    resourceTypeName: z.string().nullable(),
    /**
     * TYPEN TIL RESSURSEN POSTEN STÅR PÅ (Bar stornaust → Bar), når den står på en ressurs. Vaktas
     * «Velg ressurs» starter på typer vakta ikke allerede har (QA13 #56). Valgfri som `hasShifts`.
     */
    ownResourceTypeId: z.uuid().nullable().optional(),
    /** The person field somebody on this post must have — a key from «Personfelt og roller». */
    requiredFieldKey: z.string().nullable(),
    requiredFieldLabel: z.string().nullable(),
    /** The accreditation level a shift on this post gives (201.16). */
    accreditationLevelId: z.uuid().nullable(),
    accreditationLevelName: z.string().nullable(),
    /**
     * VAKTTIDEN (0364), «HH:MM». Null: som ressursens åpningstid. Ressursens egen tid står ved
     * siden av, så vaktplanen kan vise begge — baren åpner 18, bartenderen møter 17.
     */
    shiftStartsAt: z.string().nullable(),
    shiftEndsAt: z.string().nullable(),
    resourceOpensAt: z.string().nullable(),
    resourceClosesAt: z.string().nullable(),
    handOuts: z.array(staffingRoleHandOutSchema),
    /**
     * HAR POSTEN EN ENESTE VAKT I ÅRET (QA 26. september, M3)? En post uten vakter mangler alt, og
     * «Bare det som mangler folk» skal vise den — ellers forsvant en ny post fra vaktplanen så snart
     * siden hadde glemt at den nettopp ble laget. Valgfri: fraværende er «vet ikke», som før.
     */
    hasShifts: z.boolean().optional(),
    /** Info til dem som står på postens vakter (51, 0925). Arves av vakta. */
    note: z.string().nullable().optional(),
  })
  .meta({ id: 'StaffingRole' });
export type StaffingRoleDto = z.infer<typeof staffingRoleDtoSchema>;

export const staffingRoleListResponseSchema = z
  .object({ items: z.array(staffingRoleDtoSchema) })
  .meta({ id: 'StaffingRoleListResponse' });
export type StaffingRoleListResponse = z.infer<typeof staffingRoleListResponseSchema>;

/*
 * KVITTERINGENE FOR «ANGRE» (Atle, 23. september: «bekreftelse på å slette … og angre knapp»).
 *
 * En sletting sier hva den tok med seg, og gjenopprettingen tar tilbake NØYAKTIG det — ikke «alt
 * som ble slettet omtrent samtidig». En vakt tar med seg reservasjonene den bestilte selv; en post
 * tar med seg vaktene sine.
 */
export const shiftDeletionSchema = z
  .object({ id: z.uuid(), allocationIds: z.array(z.uuid()) })
  .meta({ id: 'ShiftDeletion' });
export type ShiftDeletion = z.infer<typeof shiftDeletionSchema>;

export const restoreShiftRequestSchema = z
  .object({ allocationIds: z.array(z.uuid()).max(500) })
  .meta({ id: 'RestoreShiftRequest' });
export type RestoreShiftRequest = z.infer<typeof restoreShiftRequestSchema>;

export const staffingRoleDeletionSchema = z
  .object({ items: z.array(staffingRoleDtoSchema), shifts: z.array(shiftDeletionSchema) })
  .meta({ id: 'StaffingRoleDeletion' });
export type StaffingRoleDeletion = z.infer<typeof staffingRoleDeletionSchema>;

export const restoreStaffingRoleRequestSchema = z
  .object({ shifts: z.array(shiftDeletionSchema).max(500) })
  .meta({ id: 'RestoreStaffingRoleRequest' });
export type RestoreStaffingRoleRequest = z.infer<typeof restoreStaffingRoleRequestSchema>;

export const listStaffingRolesQuerySchema = z
  .object({
    /**
     * ABSENT means the organisation's template, present means that year's own posts. The same
     * implicit default `listDepartmentsQuerySchema` earns for the same reason: every existing
     * caller means the template and none of them should have to say so.
     */
    editionId: z.uuid().optional(),
  })
  .meta({ id: 'ListStaffingRolesQuery' });
export type ListStaffingRolesQuery = z.infer<typeof listStaffingRolesQuerySchema>;

const roleNameSchema = z.string().trim().min(1).max(120);
/** «17:00». Samme form som ressursens åpningstid (`clockTimeSchema`), skrevet her for å holde
 *  personkontrakten fri for ressurskontrakten. */
const clockSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const createStaffingRoleRequestSchema = z
  .object({
    editionId: z.uuid().nullish(),
    /**
     * Optional since 0357: a post on a RESOURCE takes the resource's department this year, and a
     * post on a TYPE or a loose post names one. The service refuses a post with neither.
     */
    departmentId: z.uuid().nullish(),
    resourceId: z.uuid().nullish(),
    resourceTypeId: z.uuid().nullish(),
    name: roleNameSchema,
    defaultNeeded: z.number().int().min(1).max(500).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    requiredFieldKey: z.string().trim().min(1).max(60).nullish(),
    accreditationLevelId: z.uuid().nullish(),
    shiftStartsAt: clockSchema.nullish(),
    shiftEndsAt: clockSchema.nullish(),
  })
  .refine((value) => (value.shiftStartsAt == null) === (value.shiftEndsAt == null), {
    message: 'A shift time has a start and an end, or neither.',
    path: ['shiftEndsAt'],
  })
  .refine((value) => value.resourceId == null || value.resourceTypeId == null, {
    message: 'A post belongs to a resource or a type, not both.',
    path: ['resourceTypeId'],
  })
  .meta({ id: 'CreateStaffingRoleRequest' });
export type CreateStaffingRoleRequest = z.infer<typeof createStaffingRoleRequestSchema>;

export const updateStaffingRoleRequestSchema = z
  .object({
    /** Info til dem som står på postens vakter (51, 0925). */
    note: z.string().trim().min(1).max(4000).nullish(),
    departmentId: z.uuid().nullish(),
    name: roleNameSchema.optional(),
    defaultNeeded: z.number().int().min(1).max(500).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    requiredFieldKey: z.string().trim().min(1).max(60).nullish(),
    accreditationLevelId: z.uuid().nullish(),
    shiftStartsAt: clockSchema.nullish(),
    shiftEndsAt: clockSchema.nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .refine(
    (value) =>
      value.shiftStartsAt === undefined && value.shiftEndsAt === undefined
        ? true
        : (value.shiftStartsAt == null) === (value.shiftEndsAt == null),
    { message: 'A shift time has a start and an end, or neither.', path: ['shiftEndsAt'] },
  )
  .meta({ id: 'UpdateStaffingRoleRequest' });
export type UpdateStaffingRoleRequest = z.infer<typeof updateStaffingRoleRequestSchema>;

/**
 * «DELES UT PÅ VAKTA» SOM ET HELT SETT (0357). Replacing rather than adding one, for the reason the
 * assignee list is replaced: the post panel is a group of checkboxes, sent as a group.
 */
export const setStaffingRoleHandOutsRequestSchema = z
  .object({
    handOuts: z
      .array(z.object({ resourceTypeId: z.uuid(), per: handOutModeSchema.exclude(['none']) }))
      .max(20),
  })
  .meta({ id: 'SetStaffingRoleHandOutsRequest' });
export type SetStaffingRoleHandOutsRequest = z.infer<typeof setStaffingRoleHandOutsRequestSchema>;

/** «Ubemannede skift ... med prioritet». Three values — see the pg enum for why not four. */
export const SHIFT_PRIORITIES = ['normal', 'high', 'critical'] as const;
export const shiftPrioritySchema = z.enum(SHIFT_PRIORITIES);
export type ShiftPriority = z.infer<typeof shiftPrioritySchema>;

/** «Bekreftelsesstatus». `declined` is an answer, not a removal — see the pg enum. */
export const SHIFT_ASSIGNMENT_STATUSES = ['assigned', 'confirmed', 'declined'] as const;
export const shiftAssignmentStatusSchema = z.enum(SHIFT_ASSIGNMENT_STATUSES);
export type ShiftAssignmentStatus = z.infer<typeof shiftAssignmentStatusSchema>;

// ------------------------------------------------------------------------------------- shifts

const shiftTitleSchema = z.string().trim().min(1).max(120);

/** Kontaktpersonen for en vakt (42, grep C3), med grunnen. */
export const shiftContactSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    name: z.string(),
    phone: z.string().nullable(),
    reason: z.enum(['resource_owner', 'department_lead']),
  })
  .meta({ id: 'ShiftContact' });
export type ShiftContact = z.infer<typeof shiftContactSchema>;

/** Who is on a shift, resolved for display — the same shape a crew row uses for its person. */
export const shiftPersonSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    displayName: z.string().nullable(),
    /** Whether they have said yes — «bekreftelsesstatus» in the person panel. */
    status: shiftAssignmentStatusSchema,
  })
  .meta({ id: 'ShiftPerson' });

export const shiftDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    departmentId: z.uuid().nullable(),
    departmentName: z.string().nullable(),
    /** The POST. Null for a shift written before anybody set posts up — see the column. */
    roleId: z.uuid().nullable(),
    roleName: z.string().nullable(),
    /** The resource this shift staffs (0357), inherited from the post. */
    resourceId: z.uuid().nullable(),
    resourceName: z.string().nullable(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    needed: z.number().int().positive(),
    priority: shiftPrioritySchema,
    /**
     * Inline, because a shift plan is read as a whole: twenty shifts on a screen, each needing to
     * say who is on it. A request per shift would be twenty round trips to draw one day.
     */
    assigned: z.array(shiftPersonSchema),
    /**
     * RESSURSENE SOM ER TATT UT FOR VAKTA (§6, trinn 5.3).
     *
     * FLERTALL, i motsetning til rommet og sittingen: en scenevakt kan ha både et samband og en
     * bil, og de er to ting som kan være opptatt hver for seg. Rommet har ett fordi et rom ER
     * stedet man sover.
     *
     * Formen er `ContextAllocation`, den samme reisen, rommet og sittingen bruker — én form for
     * fire kontekster er ett sted å glemme at `night` finnes.
     */
    allocations: z.array(contextAllocationSchema),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
    /** Dobbeltbookingen etter skrivingen (§8.1 #5): bare i svaret på bemanning og «Velg …». */
    warnings: writeWarningsField,
    /**
     * ÅPNINGSTIDA FOR VAKTAS DAG (52, 0925). `closed`: stedet har åpningsdager i årgangen, men ikke
     * denne. `default`: ingen dager er satt, og stedets standardtid gjelder. Null uten et sted.
     */
    opening: z
      .object({
        state: z.enum(['open', 'closed', 'default']),
        opensAt: z.string().nullable(),
        closesAt: z.string().nullable(),
        /**
         * Vakta begynner før stedet åpner eller slutter etter at det stenger den dagen (260.29), eller
         * dagen er stengt. En advarsel i vaktskuffen, aldri et avslag: en bartender møter før baren
         * åpner, og det er lov.
         */
        outside: z.boolean().optional(),
      })
      .nullable()
      .optional(),
    /** Kontaktpersonen (42): ressursens ansvarlige, ellers avdelingslederen. */
    contact: shiftContactSchema.nullable().optional(),
    /** Info til den som står på vakta (51). Postens og ressursens info leses i `/shifts/:id/info`. */
    note: z.string().nullable().optional(),
  })
  .meta({ id: 'Shift' });

export const SHIFT_SORT_KEYS = ['start'] as const;
export type ShiftSortKey = (typeof SHIFT_SORT_KEYS)[number];

export const listShiftsQuerySchema = cursorQuerySchema(SHIFT_SORT_KEYS)
  .extend({
    editionId: z.uuid(),
    departmentId: z.uuid().optional(),
    /**
     * The window the plan is being read through, as instants.
     *
     * -------------------------------------------------------------------------------------------
     * WHY THE LIST IS WINDOWED RATHER THAN PAGED, FROM 1. SEPTEMBER.
     *
     * Atle: «Man kan heller ikke se vaktplan i dagene rundt festivalen, man må kunne blake
     * "uendelig" i dager og sette opp rundt arrangementet.» Build days start a week before the
     * gates and strike runs for days after, so the plan is NOT bounded by the årgang's own dates —
     * and a middling festival is 20 posts × 3 blocks × 6 days ≈ 360 shifts, which is well past
     * `MAX_PAGE_SIZE`. A cursor is the wrong shape for a grid: the reader is not walking a list,
     * they are looking at a week and then at the week before it.
     *
     * So the client asks for the days it is drawing. Both ends are inclusive of the instants given
     * — a shift is IN the window when it starts before `to` and ends after `from`, so a night
     * shift that crosses midnight belongs to both days it touches rather than falling between them.
     */
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    /** Only the slots that still need people — «ledige skift synlig». */
    understaffed: z
      .preprocess(
        (value) => (value === undefined || value === null ? undefined : String(value) === 'true'),
        z.boolean(),
      )
      .optional(),
    /** Bare vaktene uten post, i hele årgangen — «Løse vakter» (A-ressurser). */
    withoutRole: z
      .preprocess(
        (value) => (value === undefined || value === null ? undefined : String(value) === 'true'),
        z.boolean(),
      )
      .optional(),
  })
  .meta({ id: 'ListShiftsQuery' });

export const shiftListResponseSchema = page(shiftDtoSchema, 'ShiftListResponse');

export const createShiftRequestSchema = z
  .object({
    editionId: z.uuid(),
    departmentId: z.uuid().nullish(),
    roleId: z.uuid().nullish(),
    title: shiftTitleSchema,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    needed: z.number().int().min(1).max(500).default(1),
    priority: shiftPrioritySchema.optional(),
    /** «Ny vakt» har notatet også (51, H). */
    note: z.string().trim().min(1).max(4000).nullish(),
    /**
     * «RESSURS» I «NY VAKT» (258.9, regel 6): vakta reserverer ressursen for sin egen tid, som
     * «Velg ressurs» i vaktskuffen gjør. «Ny vakt» lager mange vakter, og hver får sin reservasjon.
     * Antallet er for typer som deles; null for en bil eller et rom som tas helt.
     */
    reserve: z
      .object({
        resourceId: z.uuid(),
        quantity: z.number().int().min(1).max(100_000).nullish(),
      })
      .optional(),
  })
  .refine((value) => value.endsAt > value.startsAt, {
    message: 'A shift must end after it starts.',
    path: ['endsAt'],
  })
  .meta({ id: 'CreateShiftRequest' });

export const updateShiftRequestSchema = z
  .object({
    /** Info til den som står på vakta (51, 0925). */
    note: z.string().trim().min(1).max(4000).nullish(),
    departmentId: z.uuid().nullish(),
    roleId: z.uuid().nullish(),
    title: shiftTitleSchema.optional(),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().optional(),
    needed: z.number().int().min(1).max(500).optional(),
    priority: shiftPrioritySchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateShiftRequest' });

/** The most people one request may put on a shift — the same bound co-assignees use, and why. */
export const MAX_SHIFT_ASSIGNEES = 50;

/**
 * REPLACES the whole list rather than adding one and removing one, for the reason task
 * co-assignees do: two people staffing the same shift at once must not depend on arrival order.
 */
export const setShiftAssigneesRequestSchema = z
  .object({ personIds: z.array(z.uuid()).max(MAX_SHIFT_ASSIGNEES) })
  .meta({ id: 'SetShiftAssigneesRequest' });

/**
 * One person answering for one shift — «bekreftelsesstatus».
 *
 * Its own route rather than a field on the assignee list, because replacing the list is a
 * STAFFING decision and answering is the person's own. A body that could do both would let a
 * confirmation quietly remove somebody else.
 */
export const setShiftAssignmentStatusRequestSchema = z
  .object({ personId: z.uuid(), status: shiftAssignmentStatusSchema })
  .meta({ id: 'SetShiftAssignmentStatusRequest' });
export type SetShiftAssignmentStatusRequest = z.infer<typeof setShiftAssignmentStatusRequestSchema>;

/**
 * THE PERSON'S OWN ANSWER (201.14). `declined` only when the årgang allows it — Atle, 22 September:
 * «i første omgang tildeler vi bare en vakt, og den som mottar må akseptere det uten valg å takke
 * nei». The service refuses a decline otherwise, so the setting is a rule and not a hidden button.
 */
export const respondToShiftRequestSchema = z
  .object({ status: z.enum(['confirmed', 'declined']) })
  .meta({ id: 'RespondToShiftRequest' });
export type RespondToShiftRequest = z.infer<typeof respondToShiftRequestSchema>;

/** One of MY shifts — «Mine vakter». */
export const myShiftDtoSchema = z
  .object({
    shiftId: z.uuid(),
    editionId: z.uuid(),
    /** «God Nok-Festivalen 2027» — the page lists every year I am on, so each row says which. */
    editionName: z.string(),
    /**
     * FESTIVALENS NAVN (QA13 punkt 73): årgangen heter ofte bare «2027», og et kort som sa «2027»
     * sa ikke hvor. Null på en post på en konsert, der `editionName` alt er stedet.
     */
    festivalName: z.string().nullable(),
    /** Whether that årgang lets me say no (201.14). */
    declineAllowed: z.boolean(),
    /** The årgang's own clock — a shift is read in the festival's zone, never the reader's. */
    timezone: z.string(),
    title: z.string(),
    roleName: z.string().nullable(),
    resourceName: z.string().nullable(),
    departmentName: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    status: shiftAssignmentStatusSchema,
    /**
     * VAKTA ER AVLYST SAMMEN MED ARRANGEMENTET (QA16 297.3). Personen står fortsatt på den (så «Angre»
     * gir den tilbake), men kortet sier «Avlyst» og har ingen svarknapper.
     */
    cancelled: z.boolean(),
    /** What I pick up for it: «Radiopool», «Bil 2». */
    handOuts: z.array(z.string()),
    /**
     * RESSURSENE PÅ VAKTA, MED TID (QA13 punkt 55 og 73). Bilen er reservert 12:00–14:30 på en
     * vakt som går 11:00–16:30, og et navn uten tid ga inntrykk av at den var med hele vakta.
     */
    resources: z.array(
      z.object({
        allocationId: z.uuid(),
        name: z.string(),
        title: z.string(),
        startsAt: z.iso.datetime(),
        endsAt: z.iso.datetime(),
        note: z.string().nullable(),
      }),
    ),
  })
  .meta({ id: 'MyShift' });
export type MyShiftDto = z.infer<typeof myShiftDtoSchema>;

export const myShiftListResponseSchema = z
  .object({ items: z.array(myShiftDtoSchema) })
  .meta({ id: 'MyShiftListResponse' });
export type MyShiftListResponse = z.infer<typeof myShiftListResponseSchema>;

/**
 * No `editionId` means every year, from today on — the page a notification lands on, where the
 * reader has not picked a festival. With one, only that year, past shifts included.
 */
export const listMyShiftsQuerySchema = z
  .object({ editionId: z.uuid().optional() })
  .meta({ id: 'ListMyShiftsQuery' });
export type ListMyShiftsQuery = z.infer<typeof listMyShiftsQuerySchema>;

export type CrewMemberDto = z.infer<typeof crewMemberDtoSchema>;
export type ListCrewQuery = z.infer<typeof listCrewQuerySchema>;
export type CrewListResponse = z.infer<typeof crewListResponseSchema>;
export type AddCrewMemberRequest = z.infer<typeof addCrewMemberRequestSchema>;
export type UpdateCrewMemberRequest = z.infer<typeof updateCrewMemberRequestSchema>;
export type ShiftPerson = z.infer<typeof shiftPersonSchema>;
export type ShiftDto = z.infer<typeof shiftDtoSchema>;
export type ListShiftsQuery = z.infer<typeof listShiftsQuerySchema>;
export type ShiftListResponse = z.infer<typeof shiftListResponseSchema>;
export type CreateShiftRequest = z.infer<typeof createShiftRequestSchema>;
export type UpdateShiftRequest = z.infer<typeof updateShiftRequestSchema>;
export type SetShiftAssigneesRequest = z.infer<typeof setShiftAssigneesRequestSchema>;
